import { Box, IconButton, Slide, Snackbar, Typography } from "@mui/material";
import type { SlideProps } from "@mui/material";
import {
  CheckCircleRounded,
  ErrorRounded,
  WarningRounded,
  InfoRounded,
  CloseRounded,
} from "@mui/icons-material";
import React, {
  useContext,
  useState,
  createContext,
  FC,
  ReactNode,
  useCallback,
} from "react";
import { green, red, amber, blue } from "../../theme/colors";

interface LayoutProps {
  children: ReactNode;
}

export type ToastSeverity = "success" | "error" | "warning" | "info";

interface SnackbarContextType {
  showSnackbar: (message: string, severity?: ToastSeverity) => void;
}

const FeedbackContext = createContext<SnackbarContextType | undefined>(
  undefined
);

const SEVERITY_STYLES: Record<
  ToastSeverity,
  { accent: string; Icon: typeof CheckCircleRounded }
> = {
  success: { accent: green, Icon: CheckCircleRounded },
  error: { accent: red, Icon: ErrorRounded },
  warning: { accent: amber, Icon: WarningRounded },
  info: { accent: blue, Icon: InfoRounded },
};

const SEVERITY_LABEL: Record<ToastSeverity, string> = {
  success: "Success",
  error: "Error",
  warning: "Warning",
  info: "Info",
};

const AUTO_HIDE_DURATION: Record<ToastSeverity, number> = {
  success: 3000,
  info: 3000,
  warning: 4500,
  error: 5000,
};

function detectSeverity(message: string): ToastSeverity {
  const text = message.toLowerCase();
  if (
    /error|fail|wrong|invalid|denied|cannot|unable|not found|already exist|violates|unique|duplicate|must be|is required|missing|forbidden|unauthorized|expired|not allowed/.test(
      text
    )
  ) {
    return "error";
  }
  if (/please |warning|timed out|timeout/.test(text)) {
    return "warning";
  }
  if (
    // Word-bounded so these don't false-match inside a longer word — e.g.
    // "already being processing" was matching bare "process", and an error
    // like `column "updatedAt" does not exist` was matching bare "updated".
    /\bsuccess\b|\badded\b|\bupdated\b|\bsaved\b|\bcreated\b|\bsent\b|\bstarted\b|\bstopped\b|\bprocess\b|\bunblocked\b/.test(
      text
    )
  ) {
    return "success";
  }
  return "info";
}

function SlideDownTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

const FeedbackProvide: FC<LayoutProps> = ({ children }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [severity, setSeverity] = useState<ToastSeverity>("info");

  const handleClose = (
    _?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    // ignore clickaway so an unrelated click elsewhere on the page (e.g.
    // triggering the next action) doesn't prematurely dismiss the toast
    if (reason === "clickaway") return;
    setOpen(false);
  };

  const showSnackbar = useCallback(
    (message: string, severity?: ToastSeverity) => {
      // callers frequently pass optional-chained API error text that can be
      // undefined/empty (e.g. a raw DB error with no validation `details`)
      const safeMessage = message || "Something went wrong";
      setMessage(safeMessage);
      setSeverity(severity || detectSeverity(safeMessage));
      setOpen(true);
    },
    []
  );

  const { accent, Icon } = SEVERITY_STYLES[severity];

  return (
    <FeedbackContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={AUTO_HIDE_DURATION[severity]}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={SlideDownTransition}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            minWidth: 320,
            maxWidth: 420,
            bgcolor: accent,
            borderRadius: 2.5,
            boxShadow: "0 12px 32px rgba(27,0,27,0.24)",
            px: 2,
            py: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.22)",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ color: "#FFFFFF", fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1, pt: "1px" }}>
            <Typography
              variant="subtitle2"
              sx={{ color: "#FFFFFF", fontWeight: 700, lineHeight: 1.3 }}
            >
              {SEVERITY_LABEL[severity]}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#FFFFFF",
                fontWeight: 500,
                wordBreak: "break-word",
              }}
            >
              {message}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} sx={{ mt: "-2px" }}>
            <CloseRounded sx={{ fontSize: 18, color: "rgba(255,255,255,0.85)" }} />
          </IconButton>
        </Box>
      </Snackbar>
    </FeedbackContext.Provider>
  );
};

export function useFeedback() {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error("useFeedback must be used within a FeedbackProvider");
  }
  return context;
}

export default FeedbackProvide;
